import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import {animate, state, style, transition, trigger} from '@angular/animations';

import {HeaderDataInterface} from './interfaces/header-data.interface';
import { CommonSearchFields } from '@spa/api';
import { Router } from '@angular/router';
import { PanelPaths } from 'appShared/paths';

/**
 * 'animationTransitionTimeout' value must be greater than
 * 'animationTransition' value.
 */
const animationTransition = 300;
const animationTransitionTimeout = 400;
const maxScreenSize = 1200;
const midScreenSize = 992;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    animations: [
        trigger('openClose', [
            state('open', style({
                overflow: 'hidden',
            })),
            state('closed', style({
                height: '0',
                overflow: 'hidden'
            })),
            state('shown', style({
                overflow: 'visible'
            })),
            transition('open => closed', [
                animate(animationTransition)
            ]),
            transition('closed => open', [
                animate(animationTransition)
            ]),
            transition('* => shown', [
                animate(0)
            ]),
        ])
    ]
})
export class HeaderComponent implements AfterViewInit, OnInit {
    private readonly navRightStyleVisible = 'visible';
    private readonly navRightAnimationStateClosed = 'closed';
    private readonly navRightAnimationStateOpen = 'open';
    private readonly navRightAnimationStateShown = 'shown';

    private collapseMenuButtonState = false;
    private navRightToggled = false;

    public navRightAnimationState = this.navRightAnimationStateClosed;
    public notificationToggled = false;
    public profileToggled = false;
    public commonSearchField: CommonSearchFields;
    public commonSearchKeyword: string;

    @Input() public data: HeaderDataInterface;

    @Output() public notificationBoxToggled: EventEmitter<boolean> = new EventEmitter();
    @Output() public profileBoxToggled: EventEmitter<boolean> = new EventEmitter();
    @Output() public collapseMenuButtonClicked: EventEmitter<boolean> = new EventEmitter();
    @Output() public logoutMenuButtonClick: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('navRight', {static: true}) public navRightElementRef: ElementRef;
    @ViewChild('profileBox', {static: true}) public profileBoxRef: ElementRef;

    constructor(public router: Router) {
    }

    ngOnInit(): void {
        this.commonSearchField = CommonSearchFields.All;
    }

    ngAfterViewInit() {
        setTimeout(() => {
            /**
             * 'window' is global variable for the typescript.
             */
            const windowWidth = window.innerWidth;
            if (windowWidth < maxScreenSize) {
                this.collapseMenuButtonState = true;
                this.collapseMenuButtonClicked.emit(true);

                return;
            }

            this.collapseMenuButtonState = false;
            this.collapseMenuButtonClicked.emit(false);
            this.navRightAnimationState = this.navRightAnimationStateShown;
        }, 10);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const windowWidth = event.target.innerWidth;

        this.navRightToggled = false;

        if (windowWidth < midScreenSize) {
            this.navRightAnimationState = this.navRightAnimationStateClosed;

            return;
        }
        if (windowWidth < maxScreenSize) {
            this.collapseMenuButtonState = true;
            this.collapseMenuButtonClicked.emit(true);
            this.navRightAnimationState = this.navRightAnimationStateShown;

            return;
        }

        this.collapseMenuButtonState = false;
        this.collapseMenuButtonClicked.emit(false);
        this.navRightAnimationState = this.navRightAnimationStateShown;
    }

    onCollapseMenuButtonClick() {
        this.collapseMenuButtonState = !this.collapseMenuButtonState;
        this.collapseMenuButtonClicked.emit(this.collapseMenuButtonState);
    }

    onMobileOptionClick() {
        this.navRightToggled = !this.navRightToggled;

        if (!this.navRightToggled) {
            this.navRightAnimationState = this.navRightAnimationStateClosed;

            return;
        }

        this.navRightAnimationState = this.navRightAnimationStateOpen;

        /**
         * Set 'overflow' style attribute to 'visible' after 'open' animation state execution.
         * The 'open' state set the attribute to 'hidden' for smooth animation execution.
         */
        setTimeout(() => {
            this.navRightElementRef.nativeElement.style.overflow = this.navRightStyleVisible;
        }, animationTransitionTimeout);
    }

    toggleNotificationBox() {
        this.notificationToggled = !this.notificationToggled;
        this.notificationBoxToggled.emit(this.notificationToggled);

        if (this.notificationToggled && this.profileToggled) {
            this.profileToggled = false;
            this.profileBoxToggled.emit(false);
        }
    }

    toggleProfileBox() {
        this.profileToggled = !this.profileToggled;
        this.profileBoxToggled.emit(this.profileToggled);

        if (this.profileToggled && this.notificationToggled) {
            this.notificationToggled = false;
            this.notificationBoxToggled.emit(false);
        }
    }

    onLogout() {
        this.logoutMenuButtonClick.emit(true);
    }

    onClickOutsideProfileBox(element: HTMLElement) {
        if (!this.profileBoxRef.nativeElement.contains(element)) {
            this.profileToggled = false;
            this.profileBoxToggled.emit(false);
        }
    }

    commonSearchFieldChanged(field: CommonSearchFields): void {
       this.commonSearchField = field;
       this.toggleNotificationBox();
       this.triggerSearch();
    }

    commonSearchKeywordChanged(keyword: string): void {
        this.triggerSearch();
    }

    triggerSearch(): void {
        const keyword = this.commonSearchKeyword?.trim();

        if (!keyword || keyword.length < 2) {
            return;
        }

        this.router.navigate([
            PanelPaths.root,
            PanelPaths.children.searchResults.path,
        ], {
            queryParams: {
                ['keyword']: keyword,
                ['fields']: this.commonSearchField
            }
        }).finally();
    }
}
